/*
*  optionpanel.js
*  Project: Kanoogi Platform - Main Landing Page
*
*  Created by Chris Taylor on Dec 2, 2023
*  Copyright 2021-2026 Kanoogi Inc. | All Rights Reserved.
*
*/

"use strict";

const kanoogi=window.kanoogi||{};									// Create one Kanoogi object to contain the whole shooting match
window.kanoogi=kanoogi;												// Store the master Kanoogi object into the global window object

import './style.css';
import './optionpanel.css';

import * as Module_utils from '../../Kanlibs/KAN_Utils.js';
import { AvatarDataURL } from '../../Kanlibs/KAN_Avatar.js';			// Generated default avatar (initials) when the user has no custom pic
import * as signedin from './signedin.js';							// Code that verifies if the user is signed in or not
import {BUILD_NAME} from '../build_name.js';						// Human-readable build label — bumped by hand before each rebuild, surfaced lower-left for alpha testers (gated on kanoogi.canPlay) + inside the gear dropdown stamp.
import {ShowCookieNotice} from '../../Kanlibs/KAN_CookieNotice.js';
import {ApplySigninHref} from '../../Kanlibs/KAN_SigninNext.js';	// navbar "Sign in" must come BACK here afterwards (notification deep links depend on it)
import {HasUnreadDMs, CountsAsConversation, SetPrivchatBoxDot, RememberUnreadDMs} from '../../Kanlibs/KAN_UnreadDM.js';	// ONE definition of "unread DMs", shared with KanChat's left rail

const utils=kanoogi.utils||new Module_utils.KAN_Utils();			// Initialize a new utils module if it doesn't exist
window.kanoogi.utils=utils;											// Store the master Kanoogi utils object into the global window object

let local=utils.RunningLocally();									// Can be set TRUE in production to enable debug output
const DebugOnly=utils.DebugOnly;

kanoogi.uid=0;
kanoogi.signedin=false;												// Default to signed out
kanoogi.showbrowcaps=false;											// Flag for Showing Browser Capabilities

const signin_navID=document.getElementById('signin_nav');
// navbar.html ships a bare href='signin'. Rewrite it at load so signing in
// returns the visitor to the page they were on, query string and all — this is
// the link a notification-email reader clicks when their session has lapsed,
// and a bare '/signin' silently dropped their deep link on the floor.
ApplySigninHref(signin_navID);
const loggedinpanelID=document.getElementById('loggedinpanel');

const loggedinpanelbackdropID=document.getElementById('loggedinpanelbackdrop');
const options_navID=document.getElementById('options_nav');
const optionimageID=document.getElementById('optionimage');

const usernameID=document.getElementById('username_op');

ShowCookieNotice();

 /* ---------------------------------------------------------------- */

// KeyActivate() - Make an href-less <a> behave like the button it already is.
//
// Three account controls are <a onclick=...> with no href: the mobile ☰, Options,
// and Sign out.  Such an anchor is NOT in the tab order, and even once you give it
// tabindex + role="button" the browser still won't fire click on Enter/Space — that
// behaviour is built into <button> and into <a href>, not into role alone.  The ☰
// already carried role+tabindex and so LOOKED handled; it wasn't, because nothing
// listened for the key.  Net effect before this: no keyboard path to signing out.
//
// They stay <a> rather than becoming <button> because the CSS selects them by
// element — optionpanel.css has `option_dropdown a.option-signout`, and the nav
// rules match on `a` too — so swapping the tag silently drops their styling.
//
// The panel itself is a native <dialog> opened with showModal(), which already
// traps focus and closes on Escape, so making these three focusable is the whole
// remaining gap: Tab now reaches Sign out once the panel is open.

function KeyActivate(el)
	{
	if (!el) return;
	if (!el.getAttribute('role')) el.setAttribute('role','button');
	if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex','0');
	el.addEventListener('keydown',(evt)=>
		{
		if (evt.key!=='Enter' && evt.key!==' ' && evt.key!=='Spacebar') return;
		evt.preventDefault();										// Space would otherwise scroll the page
		el.click();													// reuse the onclick already on the element
		});
	}

['menu_nav','options_nav','signout_op'].forEach(id=>KeyActivate(document.getElementById(id)));

 /* ---------------------------------------------------------------- */

// Skip-to-content link target.  The link in navbar.html points at #main by
// default; pages that haven't been migrated to id="main" still use the
// auth-gated wrapper id="renderpage", so fall back to that.  If neither
// exists (the rare interactive-only page), hide the skip link so a
// keyboard user doesn't hit a no-op Enter.
(function fixSkipLinkTarget()
	{
	const link=document.querySelector('.skip-link');
	if (!link) return;
	const target=document.getElementById('main')||document.getElementById('renderpage');
	if (target) link.setAttribute('href','#'+target.id);
	else link.style.display='none';
	})();

 /* ---------------------------------------------------------------- */

// Build-info line at the bottom of the dropdown — webpack's DefinePlugin
// replaces __KM_BUILD_TIME__ / __KM_BUILD_SHA__ with the bundle time + commit
// short SHA, and build_name.js supplies the human-readable label.  Element
// starts display:none in optionpanel.html; ValidateLogin reveals it only for
// admin or play-authorized users so regular signed-in visitors don't see internal
// build details when they open the gear dropdown.
const buildInfoID=document.getElementById('option_build_info');
if (buildInfoID)
	{
	const bTime=(typeof __KM_BUILD_TIME__!=='undefined')?__KM_BUILD_TIME__:'unknown';
	const bSha=(typeof __KM_BUILD_SHA__!=='undefined')?__KM_BUILD_SHA__:'dev';
	buildInfoID.textContent=`"${BUILD_NAME}" · ${bSha} · ${bTime}`;
	}

// Lower-left build-name chip.  Populated unconditionally at module load so
// the text is ready the moment ValidateLogin flips display from 'none' to
// '' for admin/play-authorized users.  Regular users never see the
// element (stays display:none from the HTML default).
const buildnameID=document.getElementById('buildname');
if (buildnameID) buildnameID.textContent=`build "${BUILD_NAME}"`;

 /* ---------------------------------------------------------------- */

if (typeof WebAssembly==="object") kanoogi.wasm=true;

const wasmwebgl_id=document.getElementById('wasmwebgl');

// FreeGL() - Report whether a probe context was created, then release it at once
// (the way KAN_Logo3D's probe does).  Browsers cap live WebGL contexts per page
// and silently kill the OLDEST past the cap, so a leaked probe can cost a real
// renderer its context.
function FreeGL(gl)
	{
	if (!gl) return false;
	const lose=gl.getExtension('WEBGL_lose_context');
	if (lose) lose.loseContext();
	return true;
	}

// The admin browser-caps chip: WebGPU first (the platform's first tier,
// docs/webgpu-conversion-plan.md §0), then the WebGL tiers it falls back to.
// Probed LAZILY, the first time the chip is shown - it used to create a WebGL
// context on every page load for every visitor, to feed a chip only admins see.
// Each WebGL tier gets its OWN canvas: a canvas keeps its first context type for
// life, so the old shared-canvas probe reported webgl2=false whenever webgl worked.
function RenderBrowserCaps()
	{
	if (kanoogi.webgpu===undefined)
		{
		kanoogi.webgpu=('gpu' in navigator);
		kanoogi.webgl2=FreeGL(document.createElement('canvas').getContext('webgl2'));
		kanoogi.webgl1=FreeGL(document.createElement('canvas').getContext('webgl'));
		}
	wasmwebgl_id.innerText=[kanoogi.wasm?'WASM':'', kanoogi.webgpu?'WebGPU':'', kanoogi.webgl2?'WebGL2':'', kanoogi.webgl1?'WebGL1':''].join('.');
	}
 /* ---------------------------------------------------------------- */

const resid=document.getElementById('resolution');

function RenderResolution()
	{
	const w=window.innerWidth!=null?window.innerWidth: document.body!=null?document.body.clientWidth:null;
	const h=window.innerHeight!=null?window.innerHeight: document.body!=null?document.body.clientHeight:null;
	resid.innerText=w+'x'+h;
	}
/* ---------------------------------------------------------------- */

window.addEventListener("resize",()=>
	{
	if (kanoogi.showbrowcaps) RenderResolution();						// Skip for regular users — the chip stays empty (and visually invisible) for everyone else
	},false);
 /* ---------------------------------------------------------------- */

 async function Signout()
	{
	StopUnreadPolling();											// KAN-4: stop the unread-DM poller before tearing down auth state
	localStorage.removeItem('token');								// Simply remove local JWT Token so user is logged out (but there should be more done on server to invalid token)
	localStorage.removeItem('id');

	// kan_session lives on .kanoogi.com (httpOnly) — page JS can't expire it,
	// so POST to /signout which clears it server-side.  Without this, the user
	// would still appear "signed in" on lobby.kanoogi.com until the cookie's
	// 7-day TTL expired.  Best-effort: if the fetch fails, still proceed with
	// the local-storage cleanup + redirect rather than blocking signout.
	try { await fetch('/signout',{method:'POST'}); } catch (_) {}

	window.location.href='/home';
	}
 window.Signout=Signout;
 /* ---------------------------------------------------------------- */

 function OpenOptionsPanel()
	{
	signin_navID.style.visibility='hidden';							// If signed in, don't display the "Sign In" button
	loggedinpanelbackdropID.style.visibility='visible';
	loggedinpanelID.showModal();
	// Mobile slide-in: the panel sits off-screen-right (CSS) and transitions in once
	// shown. rAF lets that off-screen start paint before the class flips it on, so it
	// animates instead of snapping. No-op on desktop (no transform there).
	requestAnimationFrame(()=>loggedinpanelID.classList.add('slide-in'));

	DebugOnly("optionpanel:Opening Options Panel");
	}
 /* ---------------------------------------------------------------- */

 function CloseOptionsPanel()
	{
	loggedinpanelID.style.visibility='visible';						// If signed in, display the "Logged in Panel"
	loggedinpanelID.classList.remove('slide-in');					// start the slide-out (mobile)
	const finishClose=()=>
		{
		loggedinpanelbackdropID.style.visibility='hidden';
		if (loggedinpanelID.open) loggedinpanelID.close();
		};
	// Let the slide-out finish before hiding on mobile; desktop has no transform
	// (no transition), so it closes immediately.
	// 1024, NOT 768: must match the nav-collapse breakpoint in style.css /
	// optionpanel.css, because the panel's off-screen + .slide-in rules live
	// inside that media block. With the CSS at 1024 and this at 768, the band
	// between them slid the panel in and then closed it with no slide-out.
	if (window.matchMedia('(max-width: 1024px)').matches) setTimeout(finishClose, 280);
	else finishClose();

	DebugOnly("optionpanel:Closing Options Panel");
	}
 /* ---------------------------------------------------------------- */

 function Options()
	{
	OpenOptionsPanel();
	}
 window.Options=Options;
 /* ---------------------------------------------------------------- */

 loggedinpanelID.addEventListener("click", event =>
	{
	const rect=loggedinpanelID.getBoundingClientRect();
	if (event.clientY<rect.top||event.clientY>rect.bottom||event.clientX<rect.left||event.clientX>rect.right)
		{
		DebugOnly("optionpanel:Click outside options panel detected");
		CloseOptionsPanel();
		}
	});
 /* ---------------------------------------------------------------- */

// Handler for the specific case of cancelling out of the option panel with an ESC key

// addEventListener, NOT document.onkeydown=. This module is imported by every
// page on the site, so the assignment form made it a land-grab: it silently
// dropped any document-level keydown handler registered before it, and was
// itself silently dropped by any registered after. Nothing collides today, but
// the next page that wants a document-level key handler would have broken
// Escape here with no error to explain it.
//
// Gated on the panel actually being open. The dialog is opened with showModal(),
// so the browser ALREADY closes it on Escape; this handler exists only to run
// the backdrop/slide-out cleanup that the native close doesn't do. Without the
// gate it ran on every Escape keypress anywhere on the site -- and
// CloseOptionsPanel is not a no-op when the panel is shut (on mobile it queues
// a 280ms timer), so it was doing work for a panel that wasn't there.
document.addEventListener('keydown',(evt)=>
	{
	if (!("key" in evt)) return;
	if (evt.key!=="Escape" && evt.key!=="Esc") return;
	if (!loggedinpanelID || !loggedinpanelID.open) return;
	CloseOptionsPanel();
	});
 /* ---------------------------------------------------------------- */

// AdjustSplork() - This isn't a real thing, it's just here as placeholder

function AdjustSplork()
	{
	DebugOnly('Splork has been adjusted');
	}
window.AdjustSplork=AdjustSplork;
/* ---------------------------------------------------------------- */

// LoggedOut() - Sets the menu visibility on/off according to state passed in

export function MenuState(state)
	{
	const id=document.getElementById('sitenavbar');

	id.style.visibility=state?'visible':'hidden';
	id.style.display=state?'':'none';								// Setting state to null is reseting to default state
	}
/* ---------------------------------------------------------------- */

// ShowSignedInTabs() - Adjusts the "signed in" tabs based on whether the client is validated or not

export function ShowSignedInTabs(validated)
	{
	// TO BE CONTINUED
	}
/* ---------------------------------------------------------------- */

const userprofileURLPrivate='https://storage.cloud.google.com/kpubdata/userprofile/pics/';

// GetUserPicURL() - Returns the URL to the users profile pic on the CDN.
// Pass the propic_version returned from /profile/load (or /users/byuid) to get a
// ?v=<n>-suffixed URL — required for the immutable Cache-Control header set in
// KanoogiMain.SaveUserProfileData to cleanly invalidate when the pic changes.

export function GetUserPicURL(userid, version)
	{
	const suffix=(version==null||version==='')?'':`?v=${encodeURIComponent(version)}`;
	return(userprofileURLPrivate+userid+'/propic.webp'+suffix);
	}
/* ---------------------------------------------------------------- */

const userprofileURLPublic='https://storage.googleapis.com/kpubdata/userprofile/pics/';

// GetUserPicURLPublic() - Returns the URL to the users profile pic on the CDN.
// Pass the propic_version returned from /profile/load (or /users/byuid) to get a
// ?v=<n>-suffixed URL — required for the immutable Cache-Control header set in
// KanoogiMain.SaveUserProfileData to cleanly invalidate when the pic changes.

export function GetUserPicURLPublic(userid, version)
	{
	const suffix=(version==null||version==='')?'':`?v=${encodeURIComponent(version)}`;
	return(userprofileURLPublic+userid+'/propic.webp'+suffix);
	}
/* ---------------------------------------------------------------- */

// LoadProfileImage() - Loads the current profile image via the web server (node.js) instead of off the kpubdata URL
// Returns an object URL, not to be confused with a resource URL/URI

export async function LoadProfileImage(which='original')
	{
	// which: 'original' = the 400x400 avatar (default — every existing caller); 'source'
	// = the private full-res re-crop source (may 404 -> returns null), used by the
	// profile editor's "Adjust photo" so the user can zoom back out past the crop.
	const proQuery=(which==='source')?'?which=source':'';
	const r=await fetch('/proimage'+proQuery,
	// const r=await fetch(`/proimage?v=${Date.now()}`,
		{
		method:'GET',
		headers:{'Content-Type':'image/webp','Authorization':`Bearer ${signedin.GetJWT()}`}
		});

	if (!r.ok) return(null);										// HTTP error (401/404/5xx) — caller treats it as "no image" and falls back (e.g. ?which=source on a legacy account)
	const datablob=await r.blob();

	if (datablob.type=='application/json')							// Did JSON response come back
		{
		DebugOnly('No image found');
		return(null);
		}

	return(URL.createObjectURL(datablob));							// Create and return an Object URL
	}
/* ---------------------------------------------------------------- */

// --- KAN-4: cross-page unread-DM dot ---
//
// Every page bundle imports optionpanel.js, so polling /dms/list here gives
// us a single global unread indicator that lights up the KanChat / KanCom
// nav tab from any page (Home, Updates, etc.), not just inside KanChat.

let unreadPollTimerId=null;											// setInterval handle so re-starts replace prior timers and Signout can clean up
const UNREAD_POLL_MS=60000;											// 60s — the dot matters most when you're NOT on KanChat (which polls its own list anyway)

// UpdateUnreadDot() - Flip every .nav-unread-dot span in the navbar.  Both
// the KanChat and KanCom nav tabs carry one (only one is visible at a time
// per the kanCom flag); we toggle both so whichever is shown reflects state.
function UpdateUnreadDot(hasUnread)
	{
	const dots=document.querySelectorAll('.nav-unread-dot');
	for (const d of dots) d.style.display=hasUnread?'inline-block':'none';

	// The navbar dot answers "is there something new"; the Direct Messages box in
	// KanChat's left rail answers "where". Lighting only the first left the user one
	// click in with no trail to follow, which is the bug this closes. No-op on every
	// page that has no rail.
	SetPrivchatBoxDot(hasUnread);

	// KanChat builds its rail after this poll may already have run, so the box needs
	// to be able to ask what the answer was.
	RememberUnreadDMs(hasUnread);
	}

// CheckUnreadOnce() - One /dms/list fetch + dot update.  Defensive on the
// response shape (raw array vs {chats:[...]}) and fails closed on network
// or auth error so a transient blip never wrongly clears the dot.
async function CheckUnreadOnce()
	{
	if (!kanoogi.signedin) return;
	try
		{
		const resp=await fetch('/dms/list',{method:'GET',headers:{'Authorization':`Bearer ${signedin.GetJWT()}`}});
		if (!resp.ok) return;
		const data=await resp.json();
		const chats=Array.isArray(data)?data:(Array.isArray(data&&data.chats)?data.chats:[]);
		/* 🔴 A chat whose ONLY member is you is a notebook, not a conversation,
		 * and it must never light the dot or send the mascot to announce it.
		 * The mascot's own note composer writes here, so without this the very
		 * next poll would tell you that you have mail — from yourself. That is
		 * the bug this dot was already fixed for once, arriving by a new door.
		 *
		 * Membership, not the chat's NAME: a name is something anybody can
		 * choose, and matching on one would let a real conversation somebody
		 * happened to title "Notes" go silently unannounced. */
		const conversations=chats.filter(CountsAsConversation);
		const unread=conversations.filter(c=>c && c.unread===true);
		UpdateUnreadDot(HasUnreadDMs(chats));

		/* Tell the mascot, if he is on this page. Same module, ~100 lines below
		 * where he is mounted, so this needs no event bus and no second poller.
		 *
		 * 🔴 A SIGNATURE, not just a count. "Unread" persists until you open the
		 * chat and the overlay is rebuilt on every navigation, so a count alone
		 * would have him announce the same message on every page you visit for
		 * as long as you ignore it. latestEidx moves when a chat actually gains
		 * an event, so the signature changes only on a genuine ARRIVAL.
		 *
		 * Sorted, because /dms/list makes no ordering promise and an unstable
		 * signature would announce the same state repeatedly.
		 *
		 * Best-effort throughout: the unread dot is the feature here and the
		 * mascot is decoration on top of it, so nothing in this block may break
		 * the dot. He simply is not on every page. */
		try
			{
			const sig=unread
				.map(c=>`${c.chatId||c.id||''}:${c.latestEidx==null?'?':c.latestEidx}`)
				.sort()
				.join(',');

			/* Where tapping the bubble should GO. Landing on the KanChat list and
			 * leaving the user to find the message themselves is most of the way
			 * to useless — the point of the bubble is to be a shortcut.
			 *
			 * KAN_Convo already reads ?forum= and ?topic= on load (the same deep
			 * link the notification emails use), so this needs no new plumbing.
			 * `forum` comes from /dms/list rather than being hardcoded: it is
			 * '_privchat' or '_privchat_qa' by environment, and guessing builds a
			 * broken URL on QA.
			 *
			 * Only when exactly ONE chat is unread. With several there is no single
			 * right destination, and picking one arbitrarily would take you to the
			 * wrong conversation half the time — the list is the honest answer. */
			// One chat page: inline or side-panel replies is a setting the page
			// reads, not a second URL to pick between.
			const chatPage='/convo';
			let href=chatPage;
			if (unread.length===1)
				{
				const c=unread[0];
				const id=c.chatId||c.id||'';
				if (id && c.forum) href=`${chatPage}?forum=${encodeURIComponent(c.forum)}&topic=${encodeURIComponent(id)}`;
				}

			const ov=await import('../../Kanlibs/KAN_MascotOverlay.js');
			const live=ov.Current&&ov.Current();
			if (live && live.setUnread) live.setUnread(unread.length,sig,href);
			}
		catch { /* no mascot on this page, or the chunk is not loaded: nothing to tell */ }
		}
	catch { /* leave existing dot state — better than flickering off on a blip */ }
	}

// StartUnreadPolling() - Idempotent: re-call replaces the prior timer.  Fires
// once immediately so the dot appears without a 60s lag after sign-in.
function StartUnreadPolling()
	{
	if (unreadPollTimerId) clearInterval(unreadPollTimerId);
	CheckUnreadOnce();
	unreadPollTimerId=setInterval(CheckUnreadOnce,UNREAD_POLL_MS);
	}

// StopUnreadPolling() - Tear down the timer and clear the dot.  Called by
// Signout so a freshly-signed-out page state doesn't keep the dot on screen.
function StopUnreadPolling()
	{
	if (unreadPollTimerId) { clearInterval(unreadPollTimerId); unreadPollTimerId=null; }
	UpdateUnreadDot(false);
	}
/* ---------------------------------------------------------------- */

// ValidateLogin() - Async function to return when login has been validated
// NOTE: It's deeply apparent that this function is not in the right place, it should be in signin
// and this code, when called, should query signin to see if the user is logged in or not
//
// 🔴 THIS FUNCTION MUST ALWAYS SETTLE.  Every .ejs page awaits it at boot and most
// reveal #renderpage only once it resolves, so a call that never comes back is a
// blank page with no error on it.  This used to be `new Promise(async(resolve)=>...)`
// with no reject and no try/catch: a throw inside the executor became an UNHANDLED
// rejection and the returned promise stayed PENDING FOREVER — it never rejected, so
// awaiting callers hung and a try/catch around the await never fired.  See the same
// note in signedin.js::SignedIn(), which is where the /validate parse actually lives
// and where the observed hang started.
//
// So: never throw, never hang.  A failure degrades to the signed-out result (uid 0),
// which is a path the UI already draws.  If you add an `await` in here, make sure it
// cannot escape the try below.

export async function ValidateLogin()
	{
	try
		{
		const {uid,username,isAdmin,canPlay,isTester,isAlpha,isRetro,canDownload,roles,mascot}=await signedin.SignedIn();	// Successfully signedin if non-zero uid

		kanoogi.uid=uid;
		kanoogi.username=username;
		kanoogi.signedin=kanoogi.uid!=0;							// Boolean to indicate signed in or not
		kanoogi.isAdmin=!!isAdmin;									// Server-computed; the UI uses this to show admin-only controls (e.g. Forum Add Topic, Dashboard nav). Backend re-checks on every admin-gated route.
		kanoogi.canPlay=!!canPlay;									// Server-computed gameplay gate result; gates launch/play affordances
		kanoogi.isTester=!!isTester;								// The tester cohort: the Store, Get, Library and play gate
		kanoogi.isAlpha=!!isAlpha;									// Cohort-backed website-feature gate for true alpha-only experiments
		kanoogi.isRetro=!!isRetro;									// Cohort gate for the retro.kanoogi.com emulator hub (super-admin OR 'retro' cohort). The Retro gateway re-checks server-side on every request.
		kanoogi.canDownload=!!canDownload;							// True when at least one current catalog audience applies (admin, PlayTester, or Retro cohort).
		kanoogi.roles=Array.isArray(roles)?roles:[];
		kanoogi.mascot=(mascot&&mascot.character)?mascot:null;		// The user's animated character, denormalized onto their account so it rides /validate — see routes/charactersets.js

		if (kanoogi.signedin)
			{
			// LoadProfileImage returns null for users with no custom pic; fall back to a generated
			// default avatar (initials, deterministic color from uid) so both the dropdown image and
			// the nav chip always show something instead of a broken-image icon.
			const propicSrc=(await LoadProfileImage())||AvatarDataURL(uid,username);

			optionimageID.src=propicSrc;
			optionimageID.style.objectFit='cover';
			optionimageID.style.display='block';

			// Mirror the same avatar onto the top-nav avatar chip so both
			// surfaces share one fetch.  #nav_propic is now a <button> (for
			// keyboard / aria reasons); the bitmap lives on its inner <img>.
			const navPropicID=document.getElementById('nav_propic');
			const navPropicImgID=document.getElementById('nav_propic_img');
			if (navPropicID && navPropicImgID)
				{
				navPropicImgID.src=propicSrc;
				navPropicID.style.display='inline-flex';
				}
			}

		// Admin or play-authorized gate for on-screen developer info (build name chip,
		// build-info row in the gear dropdown, resolution + browser-caps chips
		// at the lower edges).  Regular signed-in users see none of this so
		// the public-facing site stays free of internal build/diagnostic noise.
		const showDevInfo=kanoogi.isAdmin||kanoogi.canPlay;
		kanoogi.showbrowcaps=showDevInfo;

		// Per-user feature flags — fetched once per page bootstrap so every page
		// (chat, forums, dashboard, profile) can read kanoogi.features.<flag> when
		// deciding whether to show a UI affordance. /features is auth-gated, so
		// anonymous viewers stay on the empty defaults map and the gated affordances
		// (e.g., the KanCom nav link) remain hidden.
		kanoogi.features={};
		if (kanoogi.signedin)
			{
			try
				{
				const featResp=await fetch('/features',{method:'GET',headers:{'Authorization':`Bearer ${signedin.GetJWT()}`}});
				if (featResp.ok)
					{
					const fd=await featResp.json();
					if (fd && fd.features) kanoogi.features=fd.features;
					}
				}
			catch { /* leave empty — gated UI defaults to hidden */ }
			}

		// The user's mascot, loose on this page. Mounted here because every page
		// calls ValidateLogin() — one call site reaches the whole site — and
		// because by this point we have BOTH the character (from /validate) and
		// the `mascot` feature flag (from /features just above).
		//
		// DYNAMIC import on purpose: the character renderer is dead weight for
		// everyone who hasn't made one, so it only downloads when there is
		// actually something to draw. Failure is swallowed — a mascot must never
		// be able to break a page it is merely decorating.
		if (kanoogi.mascot && kanoogi.features.mascot!==false)
			{
			try
				{
				const overlay=await import('../../Kanlibs/KAN_MascotOverlay.js');
				// 🔴 Hand him a way to authenticate. km populates req.user ONLY
				// from `Authorization: Bearer` — the session cookie authenticates
				// PAGE loads, not the API — so his /api/weather call was answered
				// with 401 every single time and the feature had never once
				// spoken. A FUNCTION, not the token: a JWT is refreshed, and one
				// captured at mount goes stale part way through a long visit.
				// canNote: the note composer needs /dms/* and /kancom/comment, which are
		// km routes. The lobby and the Retro hub mount the same overlay and do
		// not have them, so the icon is granted here rather than assumed.
		// chatPage: where a saved note links to itself. Same page CheckUnreadOnce uses.
		const notesPage='/convo';
		// editPage: km serves the character editor, so the mascot's menu can offer a
		// way into it. Granted here for the same reason canNote is — the overlay also
		// runs on the lobby and the Retro hub, where /character/ does not exist, and a
		// menu item that 404s is worse than one that is absent.
		overlay.MountFromValidate({...kanoogi, auth:()=>signedin.GetJWT(), canNote:true, canEmailNote:true, chatPage:notesPage, editPage:'/character/'});

		/* The character editor sets a new mascot on the account and announces it
		 * on the window — it is a static page and cannot reach this bundle's
		 * overlay instance any other way. Until this listener existed the live
		 * mascot kept the OLD character until a reload, which read as "Set as
		 * mascot did nothing". Same mount, same options, new character: Mount()
		 * re-entry swaps him in place. */
		if (!window.__kanMascotListening)
			{
			window.__kanMascotListening=true;
			window.addEventListener('kanoogi:mascot',(ev)=>
				{
				const character=ev && ev.detail && ev.detail.character;
				if (!character) return;
				kanoogi.mascot={character};
				overlay.MountFromValidate({...kanoogi, auth:()=>signedin.GetJWT(), canNote:true, canEmailNote:true, chatPage:notesPage, editPage:'/character/'});
				});
			}
				}
			catch { /* no mascot today */ }
			}

		options_navID.style.visibility=kanoogi.signedin?'visible':'hidden';
		options_navID.style.display=kanoogi.signedin?'':'none';

		signin_navID.style.visibility=kanoogi.signedin?'hidden':'visible';
		signin_navID.style.display=kanoogi.signedin?'none':'';

		// Dashboard — admin-only, and it lives in THIS dropdown rather than the
		// top bar (see navbar.html). Gate is unchanged; only its home moved, so
		// the desktop nav stays down to the destinations everyone actually uses.
		for (const id of ['platform_op_label','builds_op'])
			{
			const item=document.getElementById(id);
			if (item) item.style.display=kanoogi.signedin?'':'none';
			}
		const dashboardOp=document.getElementById('dashboard_op');
		if (dashboardOp) dashboardOp.style.display=kanoogi.isAdmin?'':'none';

		// Store + Library — the tester cohort only (Chris, 2026-09-26; the Store was alpha
		// before, and "Play Games" is retired in favour of the Library). Every element
		// starts hidden in markup, so signed-out and unresolved-auth pages never flash a
		// link. /gamestore, /api/store/*, /api/library and /library repeat the gate
		// server-side; this is discoverability only.
		const showTester=(id)=>{ const el=document.getElementById(id); if (el) el.style.display=kanoogi.isTester?'':'none'; };
		showTester('gamestore_nav_li');
		showTester('m_gamestore_op');
		showTester('library_nav_li');
		showTester('library_op');

		// The catalog contains independently gated releases: PlayTesters see IGSE,
		// Retro cohort members see Zone 256 Deluxe, and admins see both. This is
		// discoverability only; both catalog and file routes re-check authorization.
		const showDownloads=kanoogi.canDownload;
		const downloadsOp=document.getElementById('downloads_op');
		const experimentalLabel=document.getElementById('experimental_op_label');
		if (downloadsOp) downloadsOp.style.display=showDownloads?'':'none';
		if (experimentalLabel) experimentalLabel.style.display=showDownloads?'':'none';

		// Kanoogiland is a general creator destination — visible to any signed-in
		// user (backend routes still require a valid session).

		// Fun Things (Character + GIF Maker) is the toy hub split out of
		// Kanoogiland. Same gate: any signed-in user. A mascot needs an account to
		// save against, so there is nothing to show a signed-out visitor.
		const funthingsOp=document.getElementById('funthings_op');
		if (funthingsOp) funthingsOp.style.display=kanoogi.signedin?'':'none';

		// Retro (the retro.kanoogi.com emulator hub) is gated on the 'retro'
		// cohort.  Same discoverability-only pattern as Desktop above: hide the
		// entry for non-members; the gateway re-checks IsRetroUser server-side
		// on every request, so this link is never the access control.
		const retroOp=document.getElementById('retro_op');
		if (retroOp) retroOp.style.display=kanoogi.isRetro?'':'none';

		// Lower-left build-name chip + gear-dropdown build-info row — admin or
		// play-authorized gate.  Elements start display:none in optionpanel.html; reveal
		// only after we've confirmed the signed-in account qualifies.
		// textContent was already set at module load, so flipping display is
		// sufficient.
		if (buildnameID) buildnameID.style.display=showDevInfo?'':'none';
		if (buildInfoID) buildInfoID.style.display=showDevInfo?'':'none';

		// Mobile in-dropdown nav (the .option-nav-m items in optionpanel.html).
		// On phones the top nav collapses into this dropdown, so these mirror the
		// top-nav gating. display='' (not 'block') lets the .option-nav-m CSS keep
		// them desktop-hidden / mobile-shown. The dropdown is reachable signed-out
		// now (the ☰ button), so the account-only rows + propic/username header are
		// hidden when logged out, and the heading reads "Menu" instead of "Options".
		const mSet=(id,show)=>{ const e=document.getElementById(id); if (e) e.style.display=show?'':'none'; };
		mSet('m_signin_op', !kanoogi.signedin);
		mSet('m_account_label', kanoogi.signedin);
		mSet('zz', kanoogi.signedin);								// Profile  — account-only
		mSet('signout_op', kanoogi.signedin);						// Sign out — account-only
		const opHeading=document.getElementById('optionheading');
		if (opHeading) opHeading.textContent=kanoogi.signedin?'Options':'Menu';
		const opImg=document.getElementById('optionimage');
		if (opImg && opImg.parentElement) opImg.parentElement.style.display=kanoogi.signedin?'':'none';
		const opUname=document.getElementById('username_op');
		if (opUname) opUname.style.display=kanoogi.signedin?'':'none';
		const navMenubar=document.querySelector('nav.menubar');
		if (navMenubar) navMenubar.classList.toggle('signed-out', !kanoogi.signedin);

		// KAN-4: keep the global unread-DM dot on the (visible) KanChat/KanCom nav
		// tab in sync with /dms/list from any page. Polling stops on Signout.
		if (kanoogi.signedin) StartUnreadPolling();

		usernameID.innerText=username;

		if (kanoogi.showbrowcaps)									// OK to shower browser capabilites?
			{
			RenderResolution();
			RenderBrowserCaps();
			}

		return({uid:uid,username:username,isAdmin:kanoogi.isAdmin,roles:kanoogi.roles});
		}
	catch(e)
		{
		// The navbar is decoration — it must never be able to hold a page hostage.
		// SignedIn() degrades rather than throwing, so getting here means the DOM
		// work above broke (a missing element on a page that doesn't ship the full
		// navbar, a propic fetch blowing up).  Put the chrome into the shape the
		// auth state calls for and hand callers whatever answer we did get, so the
		// page still reveals itself instead of waiting on a promise that never comes.
		DebugOnly('optionpanel:ValidateLogin chrome update failed - '+e);

		try
			{
			if (options_navID) { options_navID.style.visibility=kanoogi.signedin?'visible':'hidden'; options_navID.style.display=kanoogi.signedin?'':'none'; }
			if (signin_navID) { signin_navID.style.visibility=kanoogi.signedin?'hidden':'visible'; signin_navID.style.display=kanoogi.signedin?'none':''; }
			}
		catch { /* nothing left to try — just don't hang */ }

		if (!kanoogi.features) kanoogi.features={};					// Gated UI reads kanoogi.features.<flag>; never leave it undefined

		// kanoogi.* are set immediately after the fetch and cannot throw, so these
		// are the real values whenever we got that far, and signed-out shaped when
		// we didn't.
		return({uid:kanoogi.uid||0,username:kanoogi.username||'',isAdmin:!!kanoogi.isAdmin,roles:Array.isArray(kanoogi.roles)?kanoogi.roles:[]});
		}
	}
